// src/tabs/MessagesPanel.jsx — Phase 2.B (ADR-024) + 2.D ergonomie (ADR-025)
// Onglet Communication (admin only) — sous-onglets en pilules :
//   Message  : la messagerie (ex-Inbox multi-canal OTA/Mail/SMS).
//   Template : réunification VISUELLE de deux systèmes SÉPARÉS via un sélecteur
//              Plateformes | Direct —
//                • Plateformes = éditeur des templates plateformes (MessageEditor),
//                  liste groupée par flux temporel (RÉSERVATION/AVANT/JOUR J/APRÈS/ARCHIVÉS)
//                  + filtres avancés repliés (catégorie/statut/canal/source).
//                • Direct = textes du parcours réservation directe
//                  (DirectMessagesView 3 colonnes LECTURE SEULE + CancellationPoliciesEditor).
//              Les deux moteurs restent DISTINCTS, seulement rangés au même endroit.
//   (Journal : poste de pilotage LECTURE SEULE du moteur — ADR-268 — n'est
//    plus un sous-onglet : point d'entrée = bouton « Journal » en tête de la
//    page Message, composant JournalTab INTACT, rendu par InboxPanel.)
//   Demandes / Avis voyageurs : vues dédiées inchangées.
//
// DA (DESIGN.md §8 ter — « Communication → Messages, liste de templates ») :
// HABILLAGE seulement. Onglets en pilules (badge compteur corail) ; filtres repliés
// derrière un bouton « Filtres » (compteur d'actifs) ; lignes allégées (trait coloré à
// gauche = COULEUR DE SA SECTION, titre Fraunces, tags catégorie, slug technique mono
// gris discret) ; RÈGLE D'ACTIONS = action primaire corail « Éditer » + menu « ⋯ » pour
// le reste (Quick edit / Dupliquer / Désactiver / Supprimer). Sections à en-tête
// pastille teinte-catégorie + titre Fraunces + compte. Aucune logique/donnée touchée.
//
// Finitions L3 (#9/#10/#11) : (#9) trait de carte = teinte de la section d'appartenance
// (violet « Avant arrivée » conservé) ; statut conservé via une pastille ronde près du
// titre. (#10) fix popover « ⋯ » : suppression de l'overflow:hidden de la ligne qui
// clippait le menu (trait arrondi sur lui-même). (#11) bouton « Éditer » en corail.

import { useState, useEffect, useMemo, useCallback } from "react";
import {
  ClipboardList,
  Inbox,
  Mailbox,
  Star,
  Search,
  SlidersHorizontal,
  Plus,
  Pencil,
  Copy,
  Trash2,
  Pause,
  Play,
  MoreVertical,
  X,
  Clock3,
  Package,
  Ban,
  ShieldCheck,
  AlertTriangle,
  Home,
  CalendarDays,
  Puzzle,
  LogOut,
  Sparkles,
  Archive,
  Globe,
  Save,
} from "lucide-react";
import { supabase } from "../supabase";
// Le rowcount-check partagé : une écriture qui ne touche rien ne doit JAMAIS
// s'afficher comme un succès (ADR-207).
import { verifierEcriture } from "../lib/message-sends-garde";
import {
  fetchUnreadInboxCount,
  fetchPendingRequestsCount,
} from "../lib/use-notif-counters";
import { DS } from "../lib/ui";
import { useIsMobile } from "../lib/useIsMobile";
import { getAllLogsFlat } from "../lib/sousloc-helpers";
import MessageEditor, {
  isEngineActiveStatus,
  allowedOtaPlatforms,
} from "./messages/MessageEditor";
import MessagesPreviewColumn from "./messages/MessagesPreviewColumn";
import InboxPanel from "./messages/InboxPanel";
import BookingRequestsView from "./messages/BookingRequestsView";
import ModerationAvisView from "./messages/ModerationAvisView";
import DirectMessagesView from "./messages/DirectMessagesView";
import CancellationPoliciesEditor from "./messages/CancellationPoliciesEditor";
// Lot O4 — vue SMS transversale (décision Dimitri « OK vue SMS ») : gabarits +
// envois SMS toutes plateformes, LECTURE SEULE.
import SmsTransversalView from "./messages/SmsTransversalView";
// Libellés d'affichage des valeurs brutes (canal, déclencheur) — table unique.
import {
  canalShortLabel,
  UNKNOWN_TRIGGER_LABEL,
} from "./messages/textes-auto-labels";
// Onglet Journal (ADR-268) — poste de pilotage LECTURE SEULE du moteur de
// messages : prochains envois projetés + historique message_logs en clair.

// Catégorie de message → tag coloré (DA : ADMIN bleu, VOYAGEUR corail ; ménage /
// proprio en tons sémantiques distincts). Repère de lecture, pas un signal d'état.
const CATEGORIES = [
  { id: "all", label: "Toutes", bg: DS.catArdoiseBg, fg: DS.catArdoiseIco },
  { id: "voyageur", label: "Voyageur", bg: DS.corailSoft, fg: DS.corailText },
  { id: "admin", label: "Admin", bg: DS.cielSoft, fg: DS.marine },
  { id: "menage", label: "Ménage", bg: DS.warningBg, fg: DS.warningText },
  {
    id: "proprio",
    label: "Propriétaire",
    bg: DS.successBg,
    fg: DS.successText,
  },
];
const catTag = (id) => CATEGORIES.find((c) => c.id === id) || CATEGORIES[0];

// NETTOYAGE FILTRES (décision Dimitri 2026-07-08) : les options `hidden` sont
// MASQUÉES de l'UI mais leur code reste (réactivable en retirant le flag).
// Plus de bouton « Tous/Toutes » : par défaut tout s'affiche, chaque chip est
// une BASCULE (clic = filtrer, re-clic = revenir à tout). L'id interne 'all'
// reste l'état « aucun filtre ».
const STATUS_FILTERS = [
  { id: "all", label: "Tous", hidden: true },
  // ⚠️ `active_catchup` COMPTE comme Actif (il envoie réellement — sinon des
  // envois qui partent seraient cachés du filtre, décision Dimitri).
  {
    id: "active",
    label: "Actif",
    match: (sends) =>
      sends.some(
        (s) =>
          isEngineActiveStatus(s.status) &&
          (s.active_logement_ids || []).length > 0,
      ),
  },
  {
    id: "draft",
    label: "Brouillon",
    match: (sends) => sends.some((s) => s.status === "draft"),
  },
  {
    id: "paused",
    label: "En pause",
    match: (sends) => sends.some((s) => s.status === "paused"),
  },
  // Masqué : redondant avec la section ARCHIVÉS (le message y reste visible).
  {
    id: "archive",
    label: "Désactivé",
    hidden: true,
    match: (_sends, m) => !m.active,
  },
];

// Axe CANAL entièrement retiré de l'UI (peu discriminant) — logique conservée,
// réactivable via SHOW_CANAL_FILTER.
const SHOW_CANAL_FILTER = false;
const CANAL_FILTERS = [
  { id: "all", label: "Tous", hidden: true },
  { id: "email", label: "Email" },
  { id: "tchat_ota", label: "OTA" },
  { id: "notif_interne", label: "Notif interne" },
];

const SOURCE_FILTERS = [
  { id: "all", label: "Toutes", hidden: true },
  { id: "airbnb", label: "Airbnb" },
  { id: "booking", label: "Booking" },
  // Masqué (ADR-266) : aucun envoi ne cible `direct` — ce filtre ne remontait
  // que les envois universels, jamais rien de spécifique.
  { id: "direct", label: "Direct", hidden: true },
];
const ALL_SOURCES = ["airbnb", "booking", "direct"];

// Détermine si un send peut s'envoyer pour la source donnée, selon ses
// conditions JSONB. Si aucune condition n'évoque reservation.source,
// le send est "universel" (match toutes les sources).
function sendMatchesSource(send, sourceFilter) {
  if (sourceFilter === "all") return true;
  const conds = Array.isArray(send.conditions) ? send.conditions : [];
  const sourceConds = conds.filter(
    (c) => c && c.field === "reservation.source",
  );
  if (sourceConds.length === 0) return true; // universel
  // Calculer l'univers des sources autorisées
  let allowed = new Set(ALL_SOURCES);
  for (const c of sourceConds) {
    let restrict;
    if (c.op === "eq") restrict = new Set([String(c.value)]);
    else if (c.op === "neq")
      restrict = new Set(ALL_SOURCES.filter((s) => s !== String(c.value)));
    else if (c.op === "in" && Array.isArray(c.value))
      restrict = new Set(c.value.map(String));
    else continue;
    allowed = new Set([...allowed].filter((x) => restrict.has(x)));
  }
  return allowed.has(sourceFilter);
}

// ── Badges horaires inline (Phase 2.D) — icône trait fin + texte (DA, plus d'emoji) ─

function formatSendBadge(send) {
  const t = send.trigger_type;
  const off = Number(send.trigger_offset_value || 0);
  const time = send.trigger_time;
  if (t === "event_booking_created")
    return { Icon: Package, text: "à la réservation" };
  if (t === "event_booking_cancelled") return { Icon: Ban, text: "annulation" };
  if (t === "event_caution_ok")
    return { Icon: ShieldCheck, text: "à la caution" };
  if (t === "event_caution_ko")
    return { Icon: AlertTriangle, text: "caution en échec" };
  if (t === "temporal_checkin") {
    if (off < 0)
      return { Icon: Clock3, text: `J${off}${time ? " " + time : ""}` };
    if (off === 0) return { Icon: Home, text: `J0${time ? " " + time : ""}` };
    return { Icon: Clock3, text: `J+${off}${time ? " " + time : ""}` };
  }
  if (t === "temporal_checkout") {
    if (off >= 0)
      return { Icon: LogOut, text: `J+${off}${time ? " " + time : ""}` };
    return { Icon: Clock3, text: `J${off}${time ? " " + time : ""}` };
  }
  if (t === "temporal_booking")
    return {
      Icon: CalendarDays,
      text: `réservation ${off >= 0 ? "+" : ""}${off} j${time ? " " + time : ""}`,
    };
  if (t === "temporal_fixed_date")
    return { Icon: CalendarDays, text: send.trigger_fixed_date || "date fixe" };
  return { Icon: Puzzle, text: UNKNOWN_TRIGGER_LABEL };
}

// ── Sections de flux temporel (Phase 2.D ADR-025) ──────────────────
// Un send est classé dans la 1ère section qui matche. Un message peut
// apparaître dans N sections si ses sends sont dans des sections
// différentes (ex: notif_admin a 2 sends, 1 en "réservation" + 1 en
// "avant arrivée").

// Description de la section « AUTRES ENVOIS » (envois hors des 4 flux temporels
// ci-dessus). Formulée en état actuel, sans identifiants techniques ni jargon.
export const AUTRES_SENDS_DESC =
  "Envois programmés sur la date de réservation ou une date précise";

export const FLOW_SECTIONS = [
  {
    id: "reservation",
    Icon: Package,
    tint: { bg: DS.catCielBg, fg: DS.catCielIco },
    label: "RÉSERVATION REÇUE",
    desc: "Partent quand une réservation arrive ou quand une caution échoue",
    // Décision Dimitri 2026-07-08 : event_caution_ok déplacé vers AVANT
    // ARRIVÉE (le lien caution part à J-2 → la caution est forcément validée
    // avant l'arrivée). PUR AFFICHAGE — le déclencheur (webhook caution
    // validée) et le moment d'envoi ne changent pas.
    matchSend: (s) =>
      /^event_(booking_(created|cancelled)|caution_ko)$/.test(s.trigger_type),
    sortKey: () => 0, // tous mêmes — instantané
  },
  {
    id: "avant_arrivee",
    Icon: Clock3,
    tint: { bg: DS.catIndigoBg, fg: DS.catIndigoIco },
    label: "AVANT ARRIVÉE",
    desc: "Partent quelques jours avant l'arrivée, ou quand la caution est validée",
    matchSend: (s) =>
      (s.trigger_type === "temporal_checkin" &&
        Number(s.trigger_offset_value) < 0) ||
      s.trigger_type === "event_caution_ok",
    // Tri par offset desc (J-3 avant J-1) puis par heure. event_caution_ok :
    // clé « vers J-2 » (la caution se valide typiquement après le lien J-2).
    sortKey: (s) =>
      s.trigger_type === "event_caution_ok"
        ? 2 * 10000
        : -Number(s.trigger_offset_value) * 10000 + parseHHMM(s.trigger_time),
  },
  {
    id: "jour_j",
    Icon: Home,
    tint: { bg: DS.catCorailBg, fg: DS.catCorailIco },
    label: "JOUR DE L'ARRIVÉE",
    desc: "Partent le jour de l'arrivée",
    matchSend: (s) =>
      s.trigger_type === "temporal_checkin" &&
      Number(s.trigger_offset_value) === 0,
    sortKey: (s) => parseHHMM(s.trigger_time),
  },
  {
    id: "apres_depart",
    Icon: LogOut,
    tint: { bg: DS.catArdoiseBg, fg: DS.catArdoiseIco },
    label: "APRÈS DÉPART",
    desc: "Partent à partir du départ",
    matchSend: (s) =>
      s.trigger_type === "temporal_checkout" &&
      Number(s.trigger_offset_value) >= 0,
    sortKey: (s) =>
      Number(s.trigger_offset_value) * 10000 + parseHHMM(s.trigger_time),
  },
];

function parseHHMM(t) {
  if (!t) return 0;
  const m = String(t).match(/^(\d{1,2}):(\d{2})/);
  if (!m) return 0;
  return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
}

// LOT 5 — reconnaît une ligne `messages` créée POUR un envoi SMS. Le préfixe
// est posé côté serveur (SMS_SEND_SLUG_PREFIX, lib/message-sends-sms-handlers.js)
// — chaîne load-bearing, à garder synchronisée des deux côtés.
export const SMS_SEND_MESSAGE_SLUG_PREFIX = "sms_send_";
export function isSmsSendMessage(m) {
  return (
    !!m &&
    typeof m.slug === "string" &&
    m.slug.startsWith(SMS_SEND_MESSAGE_SLUG_PREFIX)
  );
}

// Catégorise tous les sends d'une liste de messages dans des sections.
// Retourne :
//   - sectionsContent : Map<sectionId, [{ message, sendsInSection }]>
//   - nouveauxSansEnvoi : messages actifs sans aucun send
//   - archives : messages avec active=false
//   - autres : sends qui ne matchent aucune section (temporal_booking, temporal_fixed_date, stubs)
function groupMessagesByFlow(messages, sendsByMsgId) {
  const sectionsContent = new Map(FLOW_SECTIONS.map((s) => [s.id, []]));
  const nouveauxSansEnvoi = [];
  const archives = [];
  const autres = []; // sends non catégorisés (bonus visuel)

  for (const m of messages) {
    if (!m.active) {
      archives.push({ message: m, sendsInSection: sendsByMsgId[m.id] || [] });
      continue;
    }

    const sends = sendsByMsgId[m.id] || [];
    if (sends.length === 0) {
      nouveauxSansEnvoi.push({ message: m, sendsInSection: [] });
      continue;
    }

    let placedInAtLeastOne = false;
    const otherSends = [];
    for (const section of FLOW_SECTIONS) {
      const matchingSends = sends.filter((s) => section.matchSend(s));
      if (matchingSends.length > 0) {
        sectionsContent.get(section.id).push({
          message: m,
          sendsInSection: matchingSends.sort(
            (a, b) => section.sortKey(a) - section.sortKey(b),
          ),
        });
        placedInAtLeastOne = true;
      }
    }
    // Sends qui n'ont pas matché aucune section (temporal_booking/fixed_date, stubs Phase ult.)
    const matchedSendIds = new Set(
      FLOW_SECTIONS.flatMap((sec) =>
        sends.filter((s) => sec.matchSend(s)).map((s) => s.id),
      ),
    );
    const unmatched = sends.filter((s) => !matchedSendIds.has(s.id));
    if (unmatched.length > 0) {
      otherSends.push({ message: m, sendsInSection: unmatched });
    }
    if (!placedInAtLeastOne && unmatched.length === 0) {
      // Pas censé arriver mais filet de sécurité
      autres.push({ message: m, sendsInSection: sends });
    } else if (otherSends.length > 0) {
      autres.push(...otherSends);
    }
  }

  return { sectionsContent, nouveauxSansEnvoi, archives, autres };
}

// ──────────────────────────────────────────────────────────────────

const SUB_TABS = [
  ["message", "Message", Inbox],
  ["template", "Template", ClipboardList],
  ["demandes", "Demandes", Mailbox],
  ["avis", "Avis voyageurs", Star],
];

// `notif` (ADR-269, optionnel) : compteurs + refreshers hissés au niveau App
// (useNotifCounters) pour alimenter le badge parent « Communication » de la
// barre principale SANS que cette section soit montée. Quand la prop est
// fournie (App.jsx), elle est la SOURCE UNIQUE des compteurs — le fallback
// local ci-dessous (état + polling 60 s historiques, mêmes requêtes via
// use-notif-counters) ne sert qu'aux usages sans App (tests, montage isolé).
// destinationThreadId / onDestinationConsommee (lot 6, prop ADDITIVE,
// précédent sansMonthNav) : ouvre la boîte de réception SUR un fil précis
// (compteurs dépliables du bandeau Voyageurs). Défaut null = rien ne change.
export default function MessagesPanel({
  cfg,
  notif,
  vue = null,
  destinationThreadId = null,
  onDestinationConsommee,
}) {
  // LOT 2 (2026-08-26) — `vue` CONTRÔLE le sous-onglet depuis le menu
  // « Voyageurs » (Messages · Textes automatiques · Avis · Demandes directes) :
  // la nav vit dans la barre secondaire de l'app, les pilules internes se
  // taisent pour ne pas doublonner. Sans `vue`, comportement historique.
  const [subTab, setSubTab] = useState(vue || "message");
  useEffect(() => {
    if (vue) setSubTab(vue);
  }, [vue]);
  const [localUnread, setLocalUnread] = useState(0);
  // Phase 5.4 Q-D5 : badge "N en attente" sur sous-onglet Demandes.
  const [localPending, setLocalPending] = useState(0);
  // Avis voyageurs : consultation LECTURE SEULE (déonto) — plus de modération,
  // donc plus de badge "en attente" sur le sous-onglet.
  const external = !!notif;

  // Compteur non-lu inbox (refresh au mount + au switch tab) — fallback local,
  // inerte quand App fournit `notif` (zéro double polling).
  const refreshLocalUnread = useCallback(async () => {
    if (external) return;
    setLocalUnread(await fetchUnreadInboxCount());
  }, [external]);
  useEffect(() => {
    refreshLocalUnread();
  }, [refreshLocalUnread]);
  // Refresh toutes les 60s pour le badge — léger (head:true count exact)
  useEffect(() => {
    if (external) return undefined;
    const id = setInterval(refreshLocalUnread, 60_000);
    return () => clearInterval(id);
  }, [external, refreshLocalUnread]);

  // Phase 5.4 — refresh pending count badge (fallback local, idem).
  const refreshLocalPending = useCallback(async () => {
    if (external) return;
    setLocalPending(await fetchPendingRequestsCount());
  }, [external]);
  useEffect(() => {
    refreshLocalPending();
  }, [refreshLocalPending]);
  useEffect(() => {
    if (external) return undefined;
    const id = setInterval(refreshLocalPending, 60_000);
    return () => clearInterval(id);
  }, [external, refreshLocalPending]);

  const unreadInbox = external ? notif.unreadInbox : localUnread;
  const pendingRequests = external ? notif.pendingRequests : localPending;
  const refreshUnread = external ? notif.refreshUnread : refreshLocalUnread;
  const refreshPendingRequests = external
    ? notif.refreshPendingRequests
    : refreshLocalPending;
  const setPendingRequests = external
    ? notif.setPendingRequests
    : setLocalPending;

  const badges = { message: unreadInbox, demandes: pendingRequests };

  return (
    <div style={{ fontFamily: DS.fontBody }}>
      {/* Sous-onglets en pilules (badge compteur corail) — masqués quand le
          menu Voyageurs (lot 2) pilote déjà la vue. */}
      <div
        style={{
          display: vue ? "none" : "flex",
          flexWrap: "wrap",
          gap: 6,
          marginBottom: 16,
        }}
      >
        {SUB_TABS.map(([id, label, Icon]) => {
          const active = subTab === id;
          const badge = badges[id] > 0 ? badges[id] : null;
          return (
            <button
              key={id}
              data-testid={`messages-subtab-${id}`}
              onClick={() => {
                setSubTab(id);
                if (id === "message") refreshUnread();
                if (id === "demandes") refreshPendingRequests();
              }}
              className="fbz-press"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 7,
                padding: "9px 15px",
                border: `0.5px solid ${active ? DS.marine : DS.border}`,
                borderRadius: 22,
                background: active ? DS.marine : DS.card,
                color: active ? "#fff" : DS.textSecondary,
                fontWeight: active ? 700 : 500,
                fontSize: 12.5,
                cursor: "pointer",
                fontFamily: DS.fontBody,
                transition: `all ${DS.durBase}`,
              }}
            >
              <Icon
                size={15}
                strokeWidth={2}
                color={active ? "#fff" : DS.textMuted}
              />
              {label}
              {badge != null && (
                <span
                  style={{
                    background: DS.corail,
                    color: "#fff",
                    borderRadius: 10,
                    padding: "1px 7px",
                    fontSize: 10,
                    fontWeight: 700,
                    minWidth: 18,
                    textAlign: "center",
                  }}
                >
                  {badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {subTab === "message" && (
        <InboxPanel
          cfg={cfg}
          onUnreadChange={refreshUnread}
          destinationThreadId={destinationThreadId}
          onDestinationConsommee={onDestinationConsommee}
        />
      )}
      {subTab === "template" && <TemplateTab cfg={cfg} />}
      {subTab === "demandes" && (
        <BookingRequestsView cfg={cfg} onPendingChange={setPendingRequests} />
      )}
      {subTab === "avis" && <ModerationAvisView cfg={cfg} />}
    </div>
  );
}

// ── Onglet Template : réunification VISUELLE de deux systèmes SÉPARÉS ────────
// Sélecteur Plateformes | Direct. Aucun moteur fusionné : sous « Plateformes »
// on rend l'éditeur des templates plateformes (MessagesTab → MessageEditor) ;
// sous « Direct » les textes du parcours réservation directe
// (DirectMessagesView 3 colonnes LECTURE SEULE + CancellationPoliciesEditor).
// Les deux restent distincts.

function TemplateTab({ cfg }) {
  const [system, setSystem] = useState("plateformes"); // 'plateformes' | 'direct'

  const SEGMENTS = [
    ["plateformes", "Plateformes"],
    ["direct", "Direct"],
    // Lot O4 — vue transversale du canal SMS (lecture seule, toutes plateformes).
    ["sms", "SMS"],
  ];

  return (
    <div>
      {/* Sélecteur de système (segmented control marine) */}
      <div
        style={{
          display: "inline-flex",
          gap: 4,
          padding: 4,
          marginBottom: 16,
          background: DS.pageBg,
          borderRadius: DS.radiusControl,
          border: `0.5px solid ${DS.border}`,
        }}
      >
        {SEGMENTS.map(([id, label]) => {
          const active = system === id;
          return (
            <button
              key={id}
              data-testid={`template-selector-${id}`}
              onClick={() => setSystem(id)}
              className="fbz-press"
              style={{
                padding: "7px 16px",
                border: "none",
                borderRadius: DS.radiusControl,
                background: active ? DS.marine : "transparent",
                color: active ? "#fff" : DS.textSecondary,
                fontWeight: active ? 700 : 600,
                fontSize: 12.5,
                cursor: "pointer",
                fontFamily: DS.fontBody,
                transition: `all ${DS.durBase}`,
              }}
            >
              {label}
            </button>
          );
        })}
      </div>

      {system === "plateformes" && <MessagesTab cfg={cfg} />}
      {system === "direct" && (
        <>
          <DirectMessagesView />
          <CancellationPoliciesEditor cfg={cfg} />
        </>
      )}
      {system === "sms" && <SmsTransversalView cfg={cfg} />}
    </div>
  );
}

// ── Onglet Messages : LOT P1 (refonte visuelle Plateformes) ─────────────────
// Layout 3 colonnes persistantes façon Direct : rail liste 380px (recherche +
// segmenté 5 rôles + filtres avancés + sections de flux) | éditeur monté en
// colonne centre (sélection dans le rail — plus de remplacement plein écran).
// La colonne APERÇU = lot P2. HABILLAGE SEULEMENT : toutes les fonctions
// existantes conservées (actions en masse, kebab, QuickEdit, filtres avancés,
// duplication multi-sections) et AUCUNE fonction d'écriture modifiée.

function MessagesTab({ cfg }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [messages, setMessages] = useState([]);
  const [sendsByMsgId, setSendsByMsgId] = useState({});
  const [search, setSearch] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterCanal, setFilterCanal] = useState("all");
  const [filterSource, setFilterSource] = useState("all");
  const [editing, setEditing] = useState(null); // null | message obj | 'new'
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [quickEdit, setQuickEdit] = useState(null); // null | message obj
  const [confirmArchive, setConfirmArchive] = useState(null); // null | { ids:[...], names:[...] }
  const [bulkBusy, setBulkBusy] = useState(false);
  // LOT P2 — brouillon vivant remonté par MessageEditor (onPreviewChange) pour
  // l'aperçu en 3e colonne. Gaté par `editing` au rendu (jamais servi périmé).
  const [previewDraft, setPreviewDraft] = useState(null);

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const [msgRes, sendRes] = await Promise.all([
        supabase.from("messages").select("*").order("category").order("slug"),
        supabase
          .from("message_sends")
          .select(
            "id, message_id, status, canal, trigger_type, trigger_offset_value, trigger_offset_unit, trigger_time, trigger_fixed_date, active_logement_ids, conditions",
          ),
      ]);
      if (msgRes.error) throw msgRes.error;
      if (sendRes.error) throw sendRes.error;
      // LOT 5 — les lignes `messages` DÉDIÉES aux envois SMS (slug préfixé
      // `sms_send_`, cf. lib/message-sends-sms-handlers.js) sont EXCLUES de
      // l'onglet Plateformes : leur corps est soumis au validateur SMS
      // (GSM-7 / 160 / whitelist) et ne s'édite QUE dans l'onglet SMS, via
      // l'action serveur. Les laisser ici rouvrirait un chemin d'écriture
      // client NON validé sur le même texte.
      setMessages((msgRes.data || []).filter((m) => !isSmsSendMessage(m)));
      const grouped = {};
      (sendRes.data || []).forEach((s) => {
        if (!grouped[s.message_id]) grouped[s.message_id] = [];
        grouped[s.message_id].push(s);
      });
      setSendsByMsgId(grouped);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    refresh();
  }, []);

  const activeFilterCount = [
    filterCategory,
    filterStatus,
    filterCanal,
    filterSource,
  ].filter((v) => v !== "all").length;

  const filtered = useMemo(() => {
    let r = messages;
    if (filterCategory !== "all")
      r = r.filter((m) => m.category === filterCategory);
    if (search.trim()) {
      const q = search.toLowerCase();
      r = r.filter(
        (m) =>
          (m.slug || "").toLowerCase().includes(q) ||
          (m.name || "").toLowerCase().includes(q) ||
          (m.description || "").toLowerCase().includes(q),
      );
    }
    // Filtres avancés (Phase 2.D) — un message match si AU MOINS UN de ses sends matche
    if (filterStatus !== "all") {
      const f = STATUS_FILTERS.find((x) => x.id === filterStatus);
      if (f && f.match)
        r = r.filter((m) => f.match(sendsByMsgId[m.id] || [], m));
    }
    if (filterCanal !== "all") {
      r = r.filter((m) =>
        (sendsByMsgId[m.id] || []).some((s) => s.canal === filterCanal),
      );
    }
    if (filterSource !== "all") {
      // Le message match si au moins 1 de ses sends peut envoyer pour cette source
      r = r.filter((m) => {
        const sends = sendsByMsgId[m.id] || [];
        if (sends.length === 0) return false; // pas de send → pas concerné par filtre source
        return sends.some((s) => sendMatchesSource(s, filterSource));
      });
    }
    return r;
  }, [
    messages,
    sendsByMsgId,
    filterCategory,
    search,
    filterStatus,
    filterCanal,
    filterSource,
  ]);

  const grouped = useMemo(
    () => groupMessagesByFlow(filtered, sendsByMsgId),
    [filtered, sendsByMsgId],
  );

  const totalDisplayed =
    grouped.nouveauxSansEnvoi.length +
    FLOW_SECTIONS.reduce(
      (acc, s) => acc + grouped.sectionsContent.get(s.id).length,
      0,
    ) +
    grouped.autres.length +
    grouped.archives.length;

  async function toggleActive(m) {
    const { error: e } = await supabase
      .from("messages")
      .update({ active: !m.active })
      .eq("id", m.id);
    if (e) {
      alert("Erreur : " + e.message);
      return;
    }
    refresh();
  }

  async function duplicate(m) {
    const newSlug = m.slug + "_copy_" + Date.now().toString(36).slice(-4);
    const { error: e } = await supabase.from("messages").insert({
      slug: newSlug,
      category: m.category,
      name: m.name + " (copie)",
      description: m.description,
      body_fr: m.body_fr,
      body_en: m.body_en,
      body_es: m.body_es,
      subject_fr: m.subject_fr,
      subject_en: m.subject_en,
      subject_es: m.subject_es,
      active: false,
    });
    if (e) {
      alert("Erreur duplication : " + e.message);
      return;
    }
    refresh();
  }

  async function deleteOne(m) {
    const { error: e } = await supabase
      .from("messages")
      .delete()
      .eq("id", m.id);
    if (e) {
      alert("Erreur suppression : " + e.message);
      return;
    }
    setConfirmDelete(null);
    refresh();
  }

  // ── Sélection multi (Phase 2.D) ──
  function toggleSelect(id) {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }
  function selectAllFiltered() {
    setSelected(new Set(filtered.map((m) => m.id)));
  }
  function clearSelection() {
    setSelected(new Set());
  }

  // ── Actions en masse (Phase 2.D) ──
  async function bulkSetSendsStatus(status) {
    if (selected.size === 0) return;
    setBulkBusy(true);
    try {
      const ids = [...selected];
      // ⚠️ `.select('id')` OBLIGATOIRE : la policy admin_update_non_sms porte
      // `canal <> 'sms'`, donc un envoi SMS matche ZÉRO ligne SANS erreur.
      // Sans ce retour, l'écran confirmait un enregistrement qui n'avait pas
      // eu lieu (ADR-207, corrigé le 2026-09-08).
      const res = await supabase
        .from("message_sends")
        .update({ status })
        .in("message_id", ids)
        .select("id");
      const verdict = verifierEcriture(res, { attendus: ids.length });
      if (!verdict.ok) throw new Error(verdict.message);
      // 'active' implique aussi de remettre messages.active=true (sinon la règle reste neutralisée par message.active=false)
      if (status === "active") {
        const { error: e2 } = await supabase
          .from("messages")
          .update({ active: true })
          .in("id", ids);
        if (e2) throw e2;
      }
      clearSelection();
      await refresh();
    } catch (e) {
      alert("Erreur action en masse : " + (e.message || e));
    } finally {
      setBulkBusy(false);
    }
  }
  async function bulkArchive() {
    if (!confirmArchive) return;
    setBulkBusy(true);
    try {
      const { error: e } = await supabase
        .from("messages")
        .update({ active: false })
        .in("id", confirmArchive.ids);
      if (e) throw e;
      setConfirmArchive(null);
      clearSelection();
      await refresh();
    } catch (e) {
      alert("Erreur archivage : " + (e.message || e));
    } finally {
      setBulkBusy(false);
    }
  }

  const cardActions = {
    setEditing,
    duplicate,
    toggleActive,
    setConfirmDelete,
    selected,
    toggleSelect,
    setQuickEdit,
    selectedMsgId: editing && editing !== "new" ? editing.id : null,
  };

  // Pessimist #936 : une action de MASSE qui touche le message OUVERT ferme
  // l'éditeur d'abord — son état est chargé au montage, un « Enregistrer »
  // ultérieur réécrirait silencieusement l'état périmé (ex. active=true sur
  // un message qu'on vient d'archiver). Appelé aux call-sites, les fonctions
  // d'écriture elles-mêmes restent byte-identiques.
  function closeEditorIfTargeted(ids) {
    if (editing && editing !== "new" && ids.includes(editing.id))
      setEditing(null);
  }

  // LOT P1 — mobile : l'éditeur remplace la liste (UX 2 niveaux conservée) ;
  // desktop : 3 colonnes persistantes (rail | éditeur), l'aperçu arrive en P2.
  const isMobile = useIsMobile();
  // BLOQUANT pessimist #936 : MessageEditor initialise son état en lazy
  // (useState(() => initial)) SANS resync — l'ancien plein écran le remontait
  // à chaque changement de message, la colonne persistante ne le fait plus.
  // key = id force le REMONTAGE à chaque changement de sélection (sinon
  // sélectionner B pendant l'édition de A laisse l'éditeur sur A → un save
  // écrirait dans le MAUVAIS message).
  const editorKey = editing === "new" ? "new" : editing ? editing.id : null;
  // Pessimist #937 : la 3e colonne (380px fixe) écraserait l'éditeur sur les
  // largeurs moyennes. Seuil sur le VIEWPORT : la sidebar de l'app (~250px)
  // + rail 380 + aperçu 380 + gaps laissent ~380px d'éditeur à 1400 — en
  // dessous, l'aperçu externe disparaît et onPreviewChange n'est pas passé →
  // MessageEditor garde son aperçu interne historique (rien de perdu).
  const showPreviewCol = !useIsMobile(1400);
  if (isMobile && editing) {
    return (
      <MessageEditor
        key={editorKey}
        cfg={cfg}
        initial={editing === "new" ? null : editing}
        onClose={() => {
          setEditing(null);
          refresh();
        }}
      />
    );
  }

  const railInner = (
    <>
      {/* Barre d'actions en masse (sticky en haut du rail quand sélection non vide) */}
      {selected.size > 0 && (
        <div
          style={{
            position: "sticky",
            top: 0,
            zIndex: 20,
            background: DS.marineHero,
            color: "#fff",
            borderRadius: DS.radiusCard,
            padding: "10px 14px",
            marginBottom: 14,
            boxShadow: "0 4px 16px rgba(0,0,0,.14)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
            flexWrap: "wrap",
          }}
        >
          <div style={{ fontSize: 12.5, fontWeight: 600 }}>
            {selected.size} message{selected.size > 1 ? "s" : ""} sélectionné
            {selected.size > 1 ? "s" : ""}
            <button
              onClick={selectAllFiltered}
              style={{
                marginLeft: 10,
                background: "transparent",
                border: "1px solid rgba(255,255,255,.4)",
                color: "#fff",
                borderRadius: 6,
                padding: "3px 9px",
                fontSize: 10,
                cursor: "pointer",
                fontFamily: DS.fontBody,
              }}
            >
              Tout sélectionner ({filtered.length})
            </button>
            <button
              onClick={clearSelection}
              style={{
                marginLeft: 6,
                background: "transparent",
                border: "1px solid rgba(255,255,255,.4)",
                color: "#fff",
                borderRadius: 6,
                padding: "3px 9px",
                fontSize: 10,
                cursor: "pointer",
                fontFamily: DS.fontBody,
              }}
            >
              Désélectionner
            </button>
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <button
              className="fbz-press"
              onClick={() => {
                closeEditorIfTargeted([...selected]);
                bulkSetSendsStatus("active");
              }}
              disabled={bulkBusy}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                background: DS.success,
                color: "#fff",
                border: "none",
                borderRadius: DS.radiusControl,
                padding: "6px 12px",
                fontSize: 11,
                fontWeight: 700,
                cursor: bulkBusy ? "wait" : "pointer",
                opacity: bulkBusy ? 0.6 : 1,
                fontFamily: DS.fontBody,
              }}
            >
              <Play size={13} strokeWidth={2.4} /> Activer
            </button>
            <button
              className="fbz-press"
              onClick={() => {
                closeEditorIfTargeted([...selected]);
                bulkSetSendsStatus("paused");
              }}
              disabled={bulkBusy}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                background: DS.warning,
                color: "#fff",
                border: "none",
                borderRadius: DS.radiusControl,
                padding: "6px 12px",
                fontSize: 11,
                fontWeight: 700,
                cursor: bulkBusy ? "wait" : "pointer",
                opacity: bulkBusy ? 0.6 : 1,
                fontFamily: DS.fontBody,
              }}
            >
              <Pause size={13} strokeWidth={2.4} /> Mettre en pause
            </button>
            <button
              className="fbz-press"
              onClick={() => {
                closeEditorIfTargeted([...selected]);
                bulkSetSendsStatus("draft");
              }}
              disabled={bulkBusy}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                background: DS.textSecondary,
                color: "#fff",
                border: "none",
                borderRadius: DS.radiusControl,
                padding: "6px 12px",
                fontSize: 11,
                fontWeight: 700,
                cursor: bulkBusy ? "wait" : "pointer",
                opacity: bulkBusy ? 0.6 : 1,
                fontFamily: DS.fontBody,
              }}
            >
              <Pencil size={13} strokeWidth={2.2} /> Brouillon
            </button>
            <button
              className="fbz-press"
              onClick={() =>
                setConfirmArchive({
                  ids: [...selected],
                  names: messages
                    .filter((m) => selected.has(m.id))
                    .map((m) => m.name),
                })
              }
              disabled={bulkBusy}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                background: DS.danger,
                color: "#fff",
                border: "none",
                borderRadius: DS.radiusControl,
                padding: "6px 12px",
                fontSize: 11,
                fontWeight: 700,
                cursor: bulkBusy ? "wait" : "pointer",
                opacity: bulkBusy ? 0.6 : 1,
                fontFamily: DS.fontBody,
              }}
            >
              <Archive size={13} strokeWidth={2.2} /> Archiver
            </button>
          </div>
        </div>
      )}

      {/* Liste — groupée par flux temporel */}
      {loading && (
        <div
          className="fbz-card"
          style={{
            padding: 14,
            color: DS.textSecondary,
            fontSize: 12,
            background: DS.card,
            borderRadius: DS.radiusCard,
            border: `0.5px solid ${DS.border}`,
          }}
        >
          Chargement…
        </div>
      )}
      {error && (
        <div
          style={{
            display: "flex",
            gap: 9,
            alignItems: "center",
            padding: "9px 12px",
            background: DS.dangerBg,
            color: DS.dangerText,
            borderRadius: 8,
            borderLeft: `3px solid ${DS.danger}`,
            fontSize: 12.5,
          }}
        >
          <AlertTriangle size={15} strokeWidth={2} style={{ flexShrink: 0 }} />{" "}
          {error}
        </div>
      )}
      {!loading && !error && totalDisplayed === 0 && (
        <div style={{ padding: "32px 16px", textAlign: "center" }}>
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: 15,
              background: DS.cielSoft,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: 11,
            }}
          >
            <ClipboardList size={26} strokeWidth={2} color={DS.marine} />
          </div>
          <div
            style={{
              fontFamily: DS.fontDisplay,
              fontSize: 16,
              fontWeight: 600,
              color: DS.marineDark,
            }}
          >
            Aucun message
          </div>
          <div style={{ fontSize: 12.5, color: DS.textMuted, marginTop: 4 }}>
            {activeFilterCount > 0 || search.trim()
              ? "Aucun résultat avec ces filtres."
              : "Créez un premier message."}
          </div>
        </div>
      )}

      {!loading && !error && grouped.nouveauxSansEnvoi.length > 0 && (
        <FlowSection
          Icon={Sparkles}
          tint={{ bg: DS.catRoseBg, fg: DS.catRoseIco }}
          label="NOUVEAUX (sans envoi programmé)"
          desc="Messages actifs qui n'ont encore aucun envoi"
          items={grouped.nouveauxSansEnvoi}
          {...cardActions}
          sendsByMsgId={sendsByMsgId}
        />
      )}
      {FLOW_SECTIONS.map((section) => {
        const items = grouped.sectionsContent.get(section.id);
        if (items.length === 0) return null;
        return (
          <FlowSection
            key={section.id}
            Icon={section.Icon}
            tint={section.tint}
            label={section.label}
            desc={section.desc}
            items={items}
            {...cardActions}
            sendsByMsgId={sendsByMsgId}
          />
        );
      })}
      {!loading && !error && grouped.autres.length > 0 && (
        <FlowSection
          Icon={Puzzle}
          tint={{ bg: DS.catArdoiseBg, fg: DS.catArdoiseIco }}
          label="AUTRES ENVOIS"
          desc={AUTRES_SENDS_DESC}
          items={grouped.autres}
          {...cardActions}
          sendsByMsgId={sendsByMsgId}
        />
      )}
      {!loading && !error && grouped.archives.length > 0 && (
        <FlowSection
          Icon={Archive}
          tint={{ bg: DS.catArdoiseBg, fg: DS.textMuted }}
          muted
          label="ARCHIVÉS"
          desc="Messages désactivés : ils ne partent plus"
          items={grouped.archives}
          {...cardActions}
          sendsByMsgId={sendsByMsgId}
        />
      )}
    </>
  );

  return (
    <div>
      {/* En-tête de page : titre + Nouveau message */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 14,
          gap: 10,
          flexWrap: "wrap",
        }}
      >
        <div>
          <div
            style={{
              fontFamily: DS.fontDisplay,
              fontSize: 17,
              fontWeight: 600,
              color: DS.marineDark,
            }}
          >
            Messages{" "}
            <span
              style={{ color: DS.textMuted, fontWeight: 500, fontSize: 14 }}
            >
              ({totalDisplayed}/{messages.length})
            </span>
          </div>
          <div
            style={{ fontSize: 11.5, color: DS.textSecondary, marginTop: 2 }}
          >
            Groupés par flux temporel · un message peut apparaître dans
            plusieurs sections.
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
          <button
            className="fbz-press"
            onClick={() => setEditing("new")}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 7,
              background: DS.corail,
              color: "#fff",
              border: "none",
              borderRadius: DS.radiusControl,
              padding: "10px 16px",
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer",
              fontFamily: DS.fontBody,
            }}
          >
            <Plus size={16} strokeWidth={2.4} /> Nouveau message
          </button>
        </div>
      </div>

      {/* LOT P1 — 3 colonnes persistantes (rail | éditeur ; aperçu = P2) */}
      <div
        style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          gap: 16,
          alignItems: "flex-start",
        }}
      >
        {/* ── Rail gauche : recherche + segmenté rôles + filtres + sections ── */}
        <div
          style={{
            width: isMobile ? "100%" : 380,
            flexShrink: 0,
            background: DS.card,
            border: `0.5px solid ${DS.border}`,
            borderRadius: DS.radiusCard,
            boxShadow: "0 10px 30px -14px rgba(20,51,80,.22)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              padding: 14,
              borderBottom: `1px solid ${DS.border}`,
              background: DS.card,
              display: "flex",
              flexDirection: "column",
              gap: 10,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 9,
                border: `0.5px solid ${DS.border}`,
                borderRadius: DS.radiusControl,
                padding: "9px 12px",
                background: DS.card,
                color: DS.textMuted,
              }}
            >
              <Search size={15} strokeWidth={2} style={{ flexShrink: 0 }} />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher un message…"
                style={{
                  flex: 1,
                  minWidth: 0,
                  border: "none",
                  outline: "none",
                  fontSize: 13,
                  background: "transparent",
                  fontFamily: DS.fontBody,
                  color: DS.textPrimary,
                }}
              />
            </div>
            {/* Segmenté 5 rôles (arbitrage Dimitri : distinction complète, compact
                pour tenir en 380px) — pilote le filtre catégorie EXISTANT. */}
            <div
              data-testid="msg-role-filter"
              style={{
                display: "flex",
                background: DS.pageBg,
                borderRadius: 9,
                padding: 3,
                gap: 2,
              }}
            >
              {CATEGORIES.map((c) => {
                const on = filterCategory === c.id;
                return (
                  <button
                    key={c.id}
                    type="button"
                    data-testid={`msg-role-filter-${c.id}`}
                    onClick={() => setFilterCategory(c.id)}
                    style={{
                      flex: 1,
                      minWidth: 0,
                      border: "none",
                      background: on ? DS.card : "transparent",
                      color: on ? DS.marine : DS.textMuted,
                      boxShadow: on ? "0 1px 3px rgba(20,51,80,.14)" : "none",
                      borderRadius: 6,
                      padding: "5px 2px",
                      fontFamily: DS.fontBody,
                      fontSize: 10.5,
                      fontWeight: 600,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 4,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {c.id !== "all" && (
                      <span
                        style={{
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          background: c.fg,
                          flexShrink: 0,
                        }}
                      />
                    )}
                    {c.id === "all"
                      ? "Tous"
                      : c.label === "Propriétaire"
                        ? "Proprio"
                        : c.label}
                  </button>
                );
              })}
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <button
                className="fbz-press"
                onClick={() => setShowFilters((v) => !v)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 7,
                  padding: "7px 12px",
                  border: `0.5px solid ${showFilters || activeFilterCount > 0 ? DS.marine : DS.border}`,
                  borderRadius: DS.radiusControl,
                  background:
                    showFilters || activeFilterCount > 0
                      ? DS.cielSoft
                      : DS.card,
                  color: DS.marine,
                  fontWeight: 600,
                  fontSize: 12,
                  cursor: "pointer",
                  fontFamily: DS.fontBody,
                  whiteSpace: "nowrap",
                }}
              >
                <SlidersHorizontal size={14} strokeWidth={2} /> Filtres
                {activeFilterCount > 0 && (
                  <span
                    style={{
                      background: DS.corail,
                      color: "#fff",
                      borderRadius: 9,
                      padding: "0px 7px",
                      fontSize: 10,
                      fontWeight: 700,
                      minWidth: 16,
                      textAlign: "center",
                    }}
                  >
                    {activeFilterCount}
                  </span>
                )}
              </button>
            </div>
            {showFilters && (
              <div
                data-testid="msg-adv-filters"
                style={{ paddingTop: 8, borderTop: `1px solid ${DS.border}` }}
              >
                {/* La Catégorie vit désormais dans le segmenté ci-dessus. */}
                <FilterChips
                  legend="Statut"
                  options={STATUS_FILTERS}
                  value={filterStatus}
                  onChange={setFilterStatus}
                />
                {SHOW_CANAL_FILTER && (
                  <FilterChips
                    legend="Canal"
                    options={CANAL_FILTERS}
                    value={filterCanal}
                    onChange={setFilterCanal}
                  />
                )}
                <FilterChips
                  legend="Source"
                  options={SOURCE_FILTERS}
                  value={filterSource}
                  onChange={setFilterSource}
                />
                {activeFilterCount > 0 && (
                  <button
                    className="fbz-press"
                    onClick={() => {
                      setFilterCategory("all");
                      setFilterStatus("all");
                      setFilterCanal("all");
                      setFilterSource("all");
                    }}
                    style={{
                      marginTop: 10,
                      background: "transparent",
                      border: "none",
                      color: DS.corail,
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: "pointer",
                      fontFamily: DS.fontBody,
                      padding: 0,
                    }}
                  >
                    Réinitialiser les filtres
                  </button>
                )}
              </div>
            )}
          </div>
          <div
            data-testid="msg-rail"
            style={{
              padding: "10px 10px 12px",
              maxHeight: isMobile ? "none" : "calc(100vh - 210px)",
              overflowY: isMobile ? "visible" : "auto",
            }}
          >
            {railInner}
          </div>
        </div>

        {/* ── Colonne centre : éditeur (sélection dans le rail) ── */}
        <div
          style={{ flex: 1, minWidth: 0, width: isMobile ? "100%" : "auto" }}
        >
          {editing ? (
            <MessageEditor
              key={editorKey}
              cfg={cfg}
              initial={editing === "new" ? null : editing}
              onClose={() => {
                setEditing(null);
                refresh();
              }}
              onPreviewChange={showPreviewCol ? setPreviewDraft : undefined}
            />
          ) : (
            <div
              className="fbz-card"
              data-testid="msg-editor-placeholder"
              style={{
                background: DS.card,
                border: `0.5px solid ${DS.border}`,
                borderRadius: DS.radiusCard,
                padding: "48px 24px",
                textAlign: "center",
                color: DS.textMuted,
              }}
            >
              <div
                style={{
                  width: 52,
                  height: 52,
                  borderRadius: 15,
                  background: DS.cielSoft,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: 11,
                }}
              >
                <ClipboardList size={26} strokeWidth={2} color={DS.marine} />
              </div>
              <div
                style={{
                  fontFamily: DS.fontDisplay,
                  fontSize: 16,
                  fontWeight: 600,
                  color: DS.marineDark,
                }}
              >
                Sélectionnez un message
              </div>
              <div style={{ fontSize: 12.5, marginTop: 4 }}>
                Cliquez sur un message dans la liste pour l'ouvrir ici, ou
                créez-en un nouveau.
              </div>
            </div>
          )}
        </div>

        {/* ── Colonne droite : APERÇU persistant (LOT P2) — desktop only,
            le mobile garde l'aperçu interne historique de l'éditeur. ── */}
        {showPreviewCol && (
          <div
            style={{ width: 380, flexShrink: 0, position: "sticky", top: 12 }}
          >
            <MessagesPreviewColumn
              cfg={cfg}
              draft={editing ? previewDraft : null}
              plats={
                editing && editing !== "new"
                  ? allowedOtaPlatforms(sendsByMsgId[editing.id])
                  : undefined
              }
            />
          </div>
        )}
      </div>

      {confirmDelete && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,.5)",
            zIndex: 50,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
        >
          <div
            className="fbz-modal-sheet"
            style={{
              background: DS.card,
              borderRadius: 14,
              padding: 22,
              maxWidth: 420,
              width: "100%",
            }}
          >
            <div
              style={{
                fontFamily: DS.fontDisplay,
                fontSize: 16,
                fontWeight: 600,
                color: DS.marineDark,
                marginBottom: 6,
              }}
            >
              Supprimer ce message ?
            </div>
            <div
              style={{
                fontSize: 12.5,
                color: DS.textSecondary,
                marginBottom: 18,
              }}
            >
              « {confirmDelete.name} ». Tous ses envois programmés seront
              supprimés aussi. C'est définitif.
            </div>
            <div
              style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}
            >
              <button
                className="fbz-press"
                onClick={() => setConfirmDelete(null)}
                style={{
                  background: DS.card,
                  color: DS.textSecondary,
                  border: `0.5px solid ${DS.borderHover}`,
                  borderRadius: DS.radiusControl,
                  padding: "8px 16px",
                  fontSize: 12.5,
                  fontWeight: 600,
                  cursor: "pointer",
                  fontFamily: DS.fontBody,
                }}
              >
                Annuler
              </button>
              <button
                className="fbz-press"
                onClick={() => deleteOne(confirmDelete)}
                style={{
                  background: DS.danger,
                  color: "#fff",
                  border: "none",
                  borderRadius: DS.radiusControl,
                  padding: "8px 16px",
                  fontSize: 12.5,
                  fontWeight: 700,
                  cursor: "pointer",
                  fontFamily: DS.fontBody,
                }}
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmArchive && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,.5)",
            zIndex: 50,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
        >
          <div
            className="fbz-modal-sheet"
            style={{
              background: DS.card,
              borderRadius: 14,
              padding: 22,
              maxWidth: 520,
              width: "100%",
            }}
          >
            <div
              style={{
                fontFamily: DS.fontDisplay,
                fontSize: 16,
                fontWeight: 600,
                color: DS.marineDark,
                marginBottom: 6,
              }}
            >
              Archiver {confirmArchive.ids.length} message
              {confirmArchive.ids.length > 1 ? "s" : ""} ?
            </div>
            <div
              style={{
                fontSize: 12.5,
                color: DS.textSecondary,
                marginBottom: 8,
              }}
            >
              Ces messages ne partiront plus. Leurs envois programmés sont
              gardés : ils repartiront si vous les réactivez (section ARCHIVÉS ›
              Activer).
            </div>
            <ul
              style={{
                fontSize: 11.5,
                color: DS.textSecondary,
                margin: "6px 0 16px 16px",
                maxHeight: 120,
                overflowY: "auto",
              }}
            >
              {confirmArchive.names.map((n, i) => (
                <li key={i}>{n}</li>
              ))}
            </ul>
            <div
              style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}
            >
              <button
                className="fbz-press"
                onClick={() => setConfirmArchive(null)}
                style={{
                  background: DS.card,
                  color: DS.textSecondary,
                  border: `0.5px solid ${DS.borderHover}`,
                  borderRadius: DS.radiusControl,
                  padding: "8px 16px",
                  fontSize: 12.5,
                  fontWeight: 600,
                  cursor: "pointer",
                  fontFamily: DS.fontBody,
                }}
              >
                Annuler
              </button>
              <button
                className="fbz-press"
                onClick={() => {
                  closeEditorIfTargeted(confirmArchive.ids);
                  bulkArchive();
                }}
                disabled={bulkBusy}
                style={{
                  background: DS.danger,
                  color: "#fff",
                  border: "none",
                  borderRadius: DS.radiusControl,
                  padding: "8px 16px",
                  fontSize: 12.5,
                  fontWeight: 700,
                  cursor: bulkBusy ? "wait" : "pointer",
                  opacity: bulkBusy ? 0.6 : 1,
                  fontFamily: DS.fontBody,
                }}
              >
                {bulkBusy ? "…" : "Archiver"}
              </button>
            </div>
          </div>
        </div>
      )}

      {quickEdit && (
        <QuickEditModal
          cfg={cfg}
          message={quickEdit}
          sends={sendsByMsgId[quickEdit.id] || []}
          onClose={(touched) => {
            setQuickEdit(null);
            if (touched) refresh();
          }}
        />
      )}
    </div>
  );
}

// ── Filter chips (legend + boutons segmented) ──────────────────────

function FilterChips({ legend, options, value, onChange }) {
  // NETTOYAGE FILTRES : options `hidden` masquées (code conservé) ; chips en
  // BASCULE — re-clic sur la chip active → retour à 'all' (tout affiché).
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        marginTop: 6,
        flexWrap: "wrap",
      }}
    >
      <span
        style={{
          fontSize: 10.5,
          color: DS.textMuted,
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: 0.4,
          minWidth: 64,
        }}
      >
        {legend}
      </span>
      <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
        {options
          .filter((o) => !o.hidden)
          .map((o) => {
            const active = value === o.id;
            return (
              <button
                key={o.id}
                className="fbz-press"
                onClick={() => onChange(active ? "all" : o.id)}
                aria-pressed={active}
                title={active ? "Cliquer pour enlever le filtre" : undefined}
                style={{
                  padding: "4px 11px",
                  border: `0.5px solid ${active ? DS.marine : DS.border}`,
                  borderRadius: 7,
                  background: active ? DS.marine : DS.card,
                  color: active ? "#fff" : DS.textSecondary,
                  fontWeight: active ? 700 : 500,
                  fontSize: 11,
                  cursor: "pointer",
                  fontFamily: DS.fontBody,
                }}
              >
                {o.label}
              </button>
            );
          })}
      </div>
    </div>
  );
}

// ── Section de flux (header pastille teinte-catégorie + cards) ─────

function FlowSection({
  Icon,
  tint,
  label,
  desc,
  muted,
  items,
  setEditing,
  duplicate,
  toggleActive,
  setConfirmDelete,
  sendsByMsgId,
  selected,
  toggleSelect,
  setQuickEdit,
  selectedMsgId,
}) {
  return (
    <div style={{ marginBottom: 12, opacity: muted ? 0.92 : 1 }}>
      {/* LOT P1 — en-tête compact façon rail (pastille icône + label + compteur). */}
      <div
        title={desc}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 9,
          padding: "6px 8px 6px 6px",
          background: tint.bg,
          borderRadius: DS.radiusControl,
          marginBottom: 5,
        }}
      >
        <span
          style={{
            width: 26,
            height: 26,
            borderRadius: 8,
            background: DS.card,
            color: tint.fg,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Icon size={15} strokeWidth={2} />
        </span>
        <span
          style={{
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: 0.8,
            textTransform: "uppercase",
            color: DS.marineDeep,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {label}
        </span>
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: DS.marineDeep,
            background: DS.card,
            padding: "1px 7px",
            borderRadius: 9,
            marginLeft: "auto",
            flexShrink: 0,
          }}
        >
          {items.length}
        </span>
      </div>
      {items.map(({ message: m, sendsInSection }, idx) => (
        <MessageCard
          key={m.id + ":" + idx}
          m={m}
          sendsInSection={sendsInSection}
          allSendsForMsg={sendsByMsgId[m.id] || []}
          isSel={selectedMsgId != null && m.id === selectedMsgId}
          setEditing={setEditing}
          duplicate={duplicate}
          toggleActive={toggleActive}
          setConfirmDelete={setConfirmDelete}
          selected={selected}
          toggleSelect={toggleSelect}
          setQuickEdit={setQuickEdit}
        />
      ))}
    </div>
  );
}

// Statut de message dérivé (pastille colorée à gauche, DA) :
//   actif = vert · pause = ambre · brouillon/aucun = gris.
function deriveStatusDot(m, sends) {
  if (!m.active) return { color: DS.textMuted, label: "désactivé" };
  // isEngineActiveStatus = active OU active_catchup (sémantique MOTEUR) —
  // aligné sur le filtre « Actif » (pessimist #941 : pastille/badge/filtre
  // doivent dire la même chose).
  if (
    sends.some(
      (s) =>
        isEngineActiveStatus(s.status) &&
        (s.active_logement_ids || []).length > 0,
    )
  )
    return { color: DS.success, label: "actif" };
  if (sends.some((s) => s.status === "paused"))
    return { color: DS.warning, label: "en pause" };
  return { color: DS.textMuted, label: "brouillon" };
}

function MessageCard({
  m,
  allSendsForMsg,
  isSel,
  setEditing,
  duplicate,
  toggleActive,
  setConfirmDelete,
  selected,
  toggleSelect,
  setQuickEdit,
}) {
  const sends = allSendsForMsg;
  const activeSends = sends.filter(
    (s) =>
      isEngineActiveStatus(s.status) &&
      (s.active_logement_ids || []).length > 0,
  );
  const tag = catTag(m.category);
  const dot = deriveStatusDot(m, sends);
  const isChecked = selected && selected.has(m.id);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState(null);

  // LOT P1 — ligne de rail compacte et CLIQUABLE (la sélection remplace le
  // bouton « Éditer » : clic = ouvre l'éditeur en colonne centre). Le kebab
  // garde Quick edit / Dupliquer / Désactiver / Supprimer ; la checkbox garde
  // la sélection multiple (stopPropagation pour ne pas ouvrir l'éditeur).
  // Sélection = contour marine complet + surélévation, fond blanc (DA Direct).
  // NB : pas d'overflow:hidden — sinon le popover « ⋯ » est clippé.
  return (
    <div
      className="fbz-rowhover"
      data-testid={`msg-row-${m.slug}`}
      role="button"
      tabIndex={0}
      onClick={() => setEditing(m)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          setEditing(m);
        }
      }}
      style={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        gap: 8,
        borderRadius: 10,
        marginBottom: 4,
        padding: "9px 9px",
        cursor: "pointer",
        border: `1.5px solid ${isSel ? DS.marine : isChecked ? DS.ciel : "transparent"}`,
        boxShadow: isSel ? "0 7px 18px -8px rgba(20,51,80,.35)" : "none",
        opacity: m.active ? 1 : 0.62,
        background: DS.card,
      }}
    >
      {toggleSelect && (
        <input
          type="checkbox"
          checked={!!isChecked}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          onChange={() => toggleSelect(m.id)}
          aria-label={`Sélectionner ${m.name}`}
          style={{
            cursor: "pointer",
            width: 15,
            height: 15,
            accentColor: DS.marine,
            flexShrink: 0,
          }}
        />
      )}
      <span
        title={dot.label}
        style={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: dot.color,
          flexShrink: 0,
        }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}
        >
          <span
            style={{
              fontFamily: DS.fontDisplay,
              fontSize: 13,
              fontWeight: 600,
              color: DS.marineDark,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {m.name}
          </span>
          {(!m.body_en || !m.body_es) && (
            <span
              title="Traduction EN/ES en attente"
              style={{
                display: "inline-flex",
                color: DS.warningText,
                flexShrink: 0,
              }}
            >
              <Globe size={11} strokeWidth={2} />
            </span>
          )}
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 5,
            marginTop: 3,
            flexWrap: "wrap",
          }}
        >
          <span
            style={{
              fontSize: 9,
              color: tag.fg,
              fontWeight: 700,
              background: tag.bg,
              padding: "1px 6px",
              borderRadius: 5,
              textTransform: "uppercase",
              letterSpacing: 0.3,
            }}
          >
            {tag.label}
          </span>
          {!m.active && (
            <span
              style={{
                fontSize: 9,
                color: DS.textMuted,
                fontWeight: 600,
                background: DS.pageBg,
                padding: "1px 6px",
                borderRadius: 5,
              }}
            >
              inactif
            </span>
          )}
          <span
            style={{
              fontSize: 9,
              color: activeSends.length > 0 ? DS.successText : DS.textMuted,
              fontWeight: 700,
              background: activeSends.length > 0 ? DS.successBg : DS.pageBg,
              padding: "1px 6px",
              borderRadius: 5,
            }}
          >
            {sends.length} envoi{sends.length > 1 ? "s" : ""}
            {activeSends.length > 0
              ? ` · ${activeSends.length} actif${activeSends.length > 1 ? "s" : ""}`
              : ""}
          </span>
          {activeSends.slice(0, 2).map((s, i) => {
            const b = formatSendBadge(s);
            const B = b.Icon;
            return (
              <span
                key={s.id || i}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 3,
                  fontSize: 9,
                  color: DS.marine,
                  background: DS.cielSoft,
                  padding: "1px 6px",
                  borderRadius: 8,
                  fontWeight: 600,
                }}
              >
                <B size={10} strokeWidth={2} />{" "}
                <span style={{ fontFamily: "monospace" }}>{b.text}</span>
              </span>
            );
          })}
          {activeSends.length > 2 && (
            <span
              title={activeSends
                .slice(2)
                .map((s) => formatSendBadge(s).text)
                .join(" · ")}
              style={{
                fontSize: 9,
                color: DS.marine,
                background: DS.cielSoft,
                padding: "1px 6px",
                borderRadius: 8,
                fontWeight: 700,
              }}
            >
              +{activeSends.length - 2}
            </span>
          )}
        </div>
      </div>

      {/* Kebab (Quick edit / Dupliquer / Désactiver / Supprimer).
          Pessimist #936 : menu en position FIXED (le rail scrolle avec
          overflow:hidden → un menu absolute serait clippé en bas de liste) ;
          les actions qui écriraient sur le message OUVERT dans l'éditeur sont
          désactivées (l'éditeur garde un état chargé au montage — agir dessus
          depuis la liste créerait un écrasement silencieux au save). */}
      <div
        style={{ display: "flex", alignItems: "center", flexShrink: 0 }}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <button
          className="fbz-press fbz-tap"
          aria-label="Plus d'actions"
          onClick={(e) => {
            const r = e.currentTarget.getBoundingClientRect();
            setMenuPos({
              top: r.bottom + 4,
              right: Math.max(8, window.innerWidth - r.right),
            });
            setMenuOpen((v) => !v);
          }}
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 28,
            height: 28,
            background: menuOpen ? DS.cielSoft : "transparent",
            color: DS.textSecondary,
            border: "none",
            borderRadius: DS.radiusControl,
            cursor: "pointer",
          }}
        >
          <MoreVertical size={16} strokeWidth={2} />
        </button>
        {menuOpen && (
          <>
            <div
              onClick={() => setMenuOpen(false)}
              onWheel={() => setMenuOpen(false)}
              style={{ position: "fixed", inset: 0, zIndex: 30 }}
            />
            <div
              style={{
                position: "fixed",
                top: menuPos?.top ?? 0,
                right: menuPos?.right ?? 0,
                zIndex: 31,
                minWidth: 188,
                background: DS.card,
                border: `0.5px solid ${DS.border}`,
                borderRadius: 10,
                boxShadow: "0 8px 28px rgba(0,0,0,.14)",
                padding: 5,
                fontFamily: DS.fontBody,
              }}
            >
              {setQuickEdit && sends.length > 0 && (
                <MenuItem
                  Icon={Clock3}
                  onClick={() => {
                    setMenuOpen(false);
                    setQuickEdit(m);
                  }}
                  label="Modification rapide"
                  disabled={isSel}
                  title={
                    isSel
                      ? "Message ouvert dans l'éditeur — réglez les envois depuis l'onglet Envois"
                      : undefined
                  }
                />
              )}
              <MenuItem
                Icon={Copy}
                onClick={() => {
                  setMenuOpen(false);
                  duplicate(m);
                }}
                label="Dupliquer"
              />
              <MenuItem
                Icon={m.active ? Pause : Play}
                onClick={() => {
                  setMenuOpen(false);
                  toggleActive(m);
                }}
                label={m.active ? "Désactiver" : "Activer"}
                color={m.active ? DS.warningText : DS.successText}
                disabled={isSel}
                title={
                  isSel
                    ? "Message ouvert dans l'éditeur — utilisez son badge de statut (ou fermez l'éditeur)"
                    : undefined
                }
              />
              <MenuItem
                Icon={Trash2}
                onClick={() => {
                  setMenuOpen(false);
                  setConfirmDelete(m);
                }}
                label="Supprimer"
                color={DS.dangerText}
                disabled={isSel}
                title={
                  isSel
                    ? "Message ouvert dans l'éditeur — fermez-le d'abord"
                    : undefined
                }
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function MenuItem({ Icon, label, onClick, color, disabled, title }) {
  return (
    <button
      className="fbz-rowhover"
      onClick={disabled ? undefined : onClick}
      disabled={!!disabled}
      title={title}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 9,
        width: "100%",
        textAlign: "left",
        background: "transparent",
        border: "none",
        borderRadius: 7,
        padding: "8px 10px",
        fontSize: 12.5,
        fontWeight: 500,
        color: disabled ? DS.textMuted : color || DS.textPrimary,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.55 : 1,
        fontFamily: DS.fontBody,
      }}
    >
      <Icon
        size={15}
        strokeWidth={2}
        color={disabled ? DS.textMuted : color || DS.textSecondary}
      />{" "}
      {label}
    </button>
  );
}

// ── QuickEditModal — édition rapide des sends d'un message ─────────
// Champs édités : status (draft/active/paused), active_logement_ids, trigger_time.
// Pas de modification du contenu, du subject, des conditions, du trigger_type.

function QuickEditModal({ cfg, message, sends, onClose }) {
  const [draftSends, setDraftSends] = useState(() =>
    sends.map((s) => ({ ...s })),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // getAllLogsFlat descend dans chambres sous-loc (lc1/lc2/lc3) — sinon
  // l'admin ne pourrait pas filtrer/activer une règle ciblant ces chambres.
  const allLogs = useMemo(
    () => getAllLogsFlat(cfg).map((l) => ({ id: l.id, nom: l.nom })),
    [cfg],
  );

  function update(idx, patch) {
    setDraftSends((prev) =>
      prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)),
    );
  }
  function toggleLogement(idx, logId) {
    setDraftSends((prev) =>
      prev.map((s, i) => {
        if (i !== idx) return s;
        const cur = s.active_logement_ids || [];
        return {
          ...s,
          active_logement_ids: cur.includes(logId)
            ? cur.filter((x) => x !== logId)
            : [...cur, logId],
        };
      }),
    );
  }

  async function saveAll() {
    setSaving(true);
    setError(null);
    try {
      // Update chaque send modifié
      for (const s of draftSends) {
        // Même garde qu'au-dessus : sans `.select('id')`, un envoi SMS
        // repartait « enregistré » sans que rien ne bouge en base.
        const res = await supabase
          .from("message_sends")
          .update({
            status: s.status,
            active_logement_ids: s.active_logement_ids,
            trigger_time: s.trigger_time,
          })
          .eq("id", s.id)
          .select("id");
        const verdict = verifierEcriture(res, { canal: s.canal });
        if (!verdict.ok) throw new Error(verdict.message);
      }
      onClose(true);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.5)",
        zIndex: 50,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        overflow: "auto",
      }}
    >
      <div
        className="fbz-modal-sheet"
        style={{
          background: DS.card,
          borderRadius: 14,
          padding: 22,
          maxWidth: 680,
          width: "100%",
          maxHeight: "90vh",
          overflow: "auto",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 14,
          }}
        >
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                fontFamily: DS.fontDisplay,
                fontSize: 16,
                fontWeight: 600,
                color: DS.marineDark,
              }}
            >
              <Clock3 size={18} strokeWidth={2} color={DS.marine} />{" "}
              Modification rapide — {message.name}
            </div>
            <div
              style={{ fontSize: 11.5, color: DS.textSecondary, marginTop: 2 }}
            >
              Statut, logements, heure. Pour modifier le contenu ou les
              conditions, utilisez « Éditer ».
            </div>
          </div>
          <button
            onClick={() => onClose(false)}
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              color: DS.textMuted,
              display: "inline-flex",
            }}
          >
            <X size={20} strokeWidth={2} />
          </button>
        </div>

        {draftSends.length === 0 && (
          <div
            style={{
              padding: "14px 16px",
              background: DS.pageBg,
              color: DS.textSecondary,
              borderRadius: 10,
              fontSize: 12,
              textAlign: "center",
            }}
          >
            Aucun envoi programmé pour ce message. Utilisez « Éditer » pour en
            ajouter.
          </div>
        )}

        {draftSends.map((s, idx) => {
          const badge = formatSendBadge(s);
          const B = badge.Icon;
          const isTemporal = s.trigger_type.startsWith("temporal_");
          return (
            <div
              key={s.id}
              style={{
                border: `0.5px solid ${DS.border}`,
                borderRadius: 10,
                padding: 12,
                marginBottom: 10,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 8,
                  flexWrap: "wrap",
                }}
              >
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    fontSize: 11,
                    color: DS.marine,
                    background: DS.cielSoft,
                    padding: "3px 9px",
                    borderRadius: 10,
                    fontWeight: 600,
                  }}
                >
                  <B size={12} strokeWidth={2} />{" "}
                  <span style={{ fontFamily: "monospace" }}>{badge.text}</span>
                </span>
                <span style={{ fontSize: 10.5, color: DS.textMuted }}>
                  {canalShortLabel(s.canal)}
                  {s.destinataire_override
                    ? " → " + s.destinataire_override
                    : ""}
                </span>
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isTemporal ? "1fr 1fr" : "1fr",
                  gap: 8,
                  marginBottom: 8,
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: 10,
                      color: DS.textMuted,
                      fontWeight: 700,
                      marginBottom: 3,
                      textTransform: "uppercase",
                      letterSpacing: 0.4,
                    }}
                  >
                    Statut
                  </div>
                  <select
                    value={s.status}
                    onChange={(e) => update(idx, { status: e.target.value })}
                    style={{
                      width: "100%",
                      border: `0.5px solid ${DS.border}`,
                      borderRadius: DS.radiusControl,
                      padding: "6px 9px",
                      fontSize: 11.5,
                      background: DS.card,
                      fontFamily: DS.fontBody,
                      color: DS.textPrimary,
                    }}
                  >
                    <option value="draft">Brouillon</option>
                    <option value="active">Actif</option>
                    <option value="paused">En pause</option>
                  </select>
                </div>
                {isTemporal && (
                  <div>
                    <div
                      style={{
                        fontSize: 10,
                        color: DS.textMuted,
                        fontWeight: 700,
                        marginBottom: 3,
                        textTransform: "uppercase",
                        letterSpacing: 0.4,
                      }}
                    >
                      Heure
                    </div>
                    <input
                      value={s.trigger_time || ""}
                      onChange={(e) =>
                        update(idx, { trigger_time: e.target.value })
                      }
                      placeholder="20:00"
                      style={{
                        width: "100%",
                        border: `0.5px solid ${DS.border}`,
                        borderRadius: DS.radiusControl,
                        padding: "6px 9px",
                        fontSize: 11.5,
                        background: DS.card,
                        fontFamily: DS.fontBody,
                        color: DS.textPrimary,
                      }}
                    />
                  </div>
                )}
              </div>
              <div>
                <div
                  style={{
                    fontSize: 10,
                    color: DS.textMuted,
                    fontWeight: 700,
                    marginBottom: 4,
                    textTransform: "uppercase",
                    letterSpacing: 0.4,
                  }}
                >
                  Logements concernés ({(s.active_logement_ids || []).length}/
                  {allLogs.length}) — Aucun logement coché = cet envoi ne part
                  jamais.
                </div>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 5,
                    padding: "7px 8px",
                    background: DS.pageBg,
                    borderRadius: 8,
                    maxHeight: 90,
                    overflowY: "auto",
                  }}
                >
                  {allLogs.map((l) => {
                    const on = (s.active_logement_ids || []).includes(l.id);
                    return (
                      <button
                        key={l.id}
                        type="button"
                        className="fbz-press"
                        onClick={() => toggleLogement(idx, l.id)}
                        title={l.id}
                        style={{
                          padding: "3px 8px",
                          border: `0.5px solid ${on ? DS.success : DS.border}`,
                          background: on ? DS.successBg : DS.card,
                          borderRadius: 6,
                          fontSize: 10,
                          cursor: "pointer",
                          color: on ? DS.successText : DS.textSecondary,
                          fontWeight: 600,
                          fontFamily: DS.fontBody,
                        }}
                      >
                        {l.nom || l.id}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 8,
            paddingTop: 10,
            borderTop: `1px solid ${DS.border}`,
          }}
        >
          {error && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "7px 12px",
                background: DS.dangerBg,
                color: DS.dangerText,
                borderRadius: 8,
                fontSize: 11,
                fontWeight: 600,
              }}
            >
              <AlertTriangle size={13} strokeWidth={2} /> {error}
            </div>
          )}
          <button
            className="fbz-press"
            onClick={() => onClose(false)}
            style={{
              background: DS.card,
              color: DS.textSecondary,
              border: `0.5px solid ${DS.borderHover}`,
              borderRadius: DS.radiusControl,
              padding: "8px 14px",
              fontSize: 12.5,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: DS.fontBody,
            }}
          >
            Annuler
          </button>
          <button
            className="fbz-press"
            onClick={saveAll}
            disabled={saving || draftSends.length === 0}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 7,
              background: DS.corail,
              color: "#fff",
              border: "none",
              borderRadius: DS.radiusControl,
              padding: "8px 18px",
              fontSize: 12.5,
              fontWeight: 700,
              cursor: saving ? "wait" : "pointer",
              opacity: saving || draftSends.length === 0 ? 0.6 : 1,
              fontFamily: DS.fontBody,
            }}
          >
            <Save size={15} strokeWidth={2} /> {saving ? "…" : "Sauver tous"}
          </button>
        </div>
      </div>
    </div>
  );
}
